/**
 * Contexto de entrada de `session_started`: ruta de entrada y referrer
 * redactados, utm_* resueltos como el resto de la atribución (URL > sello >
 * cookie) y QUÉ identificador de clic trae la visita, nunca su valor.
 */
import { CLICK_IDS, ClickId } from "./events";
import { redactPath, redactUrl } from "./redact";
import type { EntryContext } from "./tracker";

type UtmKey = "utm_source" | "utm_medium" | "utm_campaign" | "utm_content" | "utm_term";

export interface EntrySources {
  pathname: string;
  search: string;
  referrer: string | null | undefined;
  /** Valor de una clave de atribución (URL > sello > cookie): `resolveAttributionValue`. */
  resolve: (key: UtmKey | Exclude<ClickId, "ttclid">, query: URLSearchParams) => string | null;
  /** Cookie suelta (para ttclid, que no está en la atribución de la app). */
  cookie?: (name: string) => string | null;
}

const usable = (value: string | null | undefined): boolean =>
  typeof value === "string" &&
  value !== "" &&
  value !== "null" &&
  value !== "undefined" &&
  value !== "defined";

/** Referrer sin query string ni fragmento y con la ruta redactada (pueden llevar datos personales). */
export const cleanReferrer = (referrer: string | null | undefined): string | null =>
  redactUrl(referrer);

/** El primer identificador de clic de la visita, en el orden del contrato. */
export const clickIdOf = (sources: Omit<EntrySources, "pathname" | "referrer">): ClickId | null => {
  const query = new URLSearchParams(sources.search);
  const found = CLICK_IDS.find((key) =>
    key === "ttclid"
      ? usable(query.get(key)) || usable(sources.cookie?.(key))
      : usable(sources.resolve(key, query))
  );
  return found ?? null;
};

export const entryContext = (sources: EntrySources): EntryContext => {
  const query = new URLSearchParams(sources.search);
  const utm = (key: UtmKey) => sources.resolve(key, query);
  return {
    entry_path: redactPath(sources.pathname || "/"),
    referrer: cleanReferrer(sources.referrer),
    utm_source: utm("utm_source"),
    utm_medium: utm("utm_medium"),
    utm_campaign: utm("utm_campaign"),
    utm_content: utm("utm_content"),
    utm_term: utm("utm_term"),
    click_id: clickIdOf(sources),
  };
};
